import { Capacitor, registerPlugin } from "@capacitor/core";
import { triggerBlobDownload } from "@/lib/downloadFile";

export const FILE_DELIVERY_BYTE_LIMIT = 5 * 1024 * 1024;
type NativeReply = { status: "saved" | "cancelled"; bytes?: number };
interface NativeFileDeliveryPlugin {
  save(input: { requestId: string; base64: string; fileName: string; mimeType: string }): Promise<NativeReply>;
  cancel(input: { requestId: string }): Promise<void>;
}
const NativeFileDelivery = registerPlugin<NativeFileDeliveryPlugin>("NativeFileDelivery");

export interface FileDeliveryInput {
  base64: string;
  fileName: string;
  mimeType: string;
  /** The captured account/session/generation must still own this request. */
  isCurrent: () => boolean;
}
export interface FileDeliveryResult {
  status: "saved" | "cancelled";
  destination: "native" | "browser";
}
function validatedFile(input: FileDeliveryInput): { bytes: Uint8Array; name: string } {
  if (typeof input.base64 !== "string" || input.base64.length > Math.ceil(FILE_DELIVERY_BYTE_LIMIT / 3) * 4 ||
      input.base64.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(input.base64))
    throw new Error("invalid_file_delivery");
  let binary: string;
  try { binary = atob(input.base64); } catch { throw new Error("invalid_file_delivery"); }
  if (binary.length > FILE_DELIVERY_BYTE_LIMIT || btoa(binary) !== input.base64)
    throw new Error("invalid_file_delivery");
  if (typeof input.fileName !== "string" || !input.fileName || input.fileName !== input.fileName.trim() ||
      new TextEncoder().encode(input.fileName).length > 240 || /[\\/:]/.test(input.fileName) ||
      Array.from(input.fileName).some(char => { const point = char.codePointAt(0)!; return point < 32 || point >= 127 && point <= 159; }) ||
      input.fileName === "." || input.fileName === ".." ||
      typeof input.mimeType !== "string" || input.mimeType.length > 128 || !/^[A-Za-z0-9!#$&^_.+-]+\/[A-Za-z0-9!#$&^_.+-]+$/.test(input.mimeType))
    throw new Error("invalid_file_delivery");
  return { bytes: Uint8Array.from(binary, char => char.charCodeAt(0)), name: input.fileName };
}

/** Validated bytes, an explicit native destination, and a captured session fence. */
export async function deliverBase64File(input: FileDeliveryInput): Promise<FileDeliveryResult> {
  const native = Capacitor.isNativePlatform();
  const destination = native ? "native" : "browser";
  const current = () => { try { return input.isCurrent() === true; } catch { return false; } };
  if (!current()) return { status: "cancelled", destination };
  const file = validatedFile(input);
  if (!current()) return { status: "cancelled", destination };
  if (!native) {
    triggerBlobDownload(new Blob([new Uint8Array(file.bytes).buffer], { type: input.mimeType }), file.name);
    // Browser acknowledgement is dispatch only; callers must not claim persisted disk evidence.
    return { status: "saved", destination };
  }
  if (!Capacitor.isPluginAvailable("NativeFileDelivery")) throw new Error("native_file_delivery_unavailable");
  const requestId = crypto.randomUUID();
  let cancelled = false;
  const cancel = () => {
    if (cancelled) return;
    cancelled = true;
    void NativeFileDelivery.cancel({ requestId }).catch(() => undefined);
  };
  const timer = window.setInterval(() => { if (!current()) cancel(); }, 100);
  try {
    if (!current()) { cancel(); return { status: "cancelled", destination }; }
    const reply = await NativeFileDelivery.save({
      requestId, base64: input.base64, fileName: file.name, mimeType: input.mimeType,
    });
    if (cancelled || !current()) { cancel(); return { status: "cancelled", destination }; }
    if (reply?.status === "cancelled") return { status: "cancelled", destination };
    if (reply?.status !== "saved" || reply.bytes !== file.bytes.byteLength)
      throw new Error("file_delivery_unconfirmed");
    return { status: "saved", destination };
  } finally {
    window.clearInterval(timer);
  }
}
